import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';
import { useFacility } from '../../context/FacilityContext';

/**
 * Marketing engine — the CONNECTION widget, in Third-party apps alongside
 * the other integrations (owner, 2026-08-14). Provider-neutral (owner,
 * 2026-08-29): the marketing engine is a microservice each facility
 * connects to, and a facility may use the engine of its choice — so the
 * engine URL is editable and stored per facility (the platform default is
 * only prefilled). Each facility is its own tenant with its own key: the
 * key is posted to the server, stored encrypted, and never returned to the
 * browser again.
 *
 * Host-styled on purpose: the engine's own design language is scoped to
 * the marketing surface itself; this is a settings card and matches its
 * neighbours.
 */
const MasConnectionCard = () => {
  const { facilityId } = useFacility();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engineUrl, setEngineUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await apiClient.get('/api/mas/panel/connection', {
        params: { facility_id: facilityId },
      });
      setStatus(res.data);
      setEngineUrl(res.data?.engine_url || '');
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!engineUrl.trim()) {
      toast.error('Enter the engine URL for this facility.');
      return;
    }
    if (!apiKey.trim() && !status?.connected) {
      toast.error('Paste this facility’s tenant key.');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await apiClient.post('/api/mas/panel/connect', {
        facility_id: facilityId,
        engine_url: engineUrl.trim(),
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        ...(secret.trim() ? { webhook_secret: secret.trim() } : {}),
      });
      setResult(res.data);
      if (res.data.connected) {
        toast.success(apiKey.trim()
          ? 'Connected to the marketing engine.'
          : 'Connection verified.');
        setApiKey('');
        setSecret('');
        await load();
      } else if (res.data?.error?.message) {
        toast.error(res.data.error.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  if (!facilityId) return null;

  const connected = status?.connected;
  const err = result && !result.connected ? result.error : null;
  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-[12px] font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Megaphone className="h-4 w-4 text-slate-500" />
        <h3 className="text-[14px] font-semibold text-slate-900">Marketing engine</h3>
        {loading ? null : connected ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-medium">
            <CheckCircle2 className="h-3 w-3" /> Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">
            Not connected
          </span>
        )}
      </div>
      <p className="text-[12px] text-slate-500 mb-3">
        Each facility is its own tenant on the engine you choose — its own
        key, its own marketing. Once connected, the engine runs this
        facility&rsquo;s website, listings, reviews, social and follow-ups,
        and the results appear under Marketing. Values come from your
        marketing engine provider.
      </p>

      {loading ? (
        <p className="text-[12px] text-slate-400">Loading…</p>
      ) : (
        <>
          {connected && (
            <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[12px] text-emerald-800">
              Connected as <strong>{status.tenant_name}</strong>
              {status.connected_at && (
                <span className="text-emerald-700/70"> · since {String(status.connected_at).slice(0, 10)}</span>
              )}
              <Link to="/marketing" className="ml-2 text-emerald-800 underline hover:no-underline">
                Open marketing
              </Link>
            </div>
          )}

          <div className="space-y-3">
            {err === 'key_rejected' && (
              <p className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                The engine rejected that key. Ask your provider for a
                current key for this facility.
              </p>
            )}
            {err === 'unreachable' && (
              <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                The engine at that URL didn’t respond. Check the address and
                that it’s running, then try again.
              </p>
            )}

            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                Engine URL — this facility
              </label>
              <input value={engineUrl} onChange={(e) => setEngineUrl(e.target.value)}
                placeholder="https://marketing.example.com"
                autoComplete="off" spellCheck={false} className={inputCls}
                data-testid="mas-engine-url" />
              <p className="text-[11px] text-slate-400 mt-1">
                {status?.default_engine_url && engineUrl === status.default_engine_url
                  ? 'The platform default. Replace it to use a different marketing engine for this facility.'
                  : 'Any engine that speaks the marketing tenant handshake. Stored per facility.'}
              </p>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                Tenant API key — {status?.facility_name}
              </label>
              <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder={connected
                  ? '•••• stored — leave blank to keep'
                  : 'Paste the key your provider issued for this facility'}
                autoComplete="off" spellCheck={false} className={inputCls} />
              <p className="text-[11px] text-slate-400 mt-1">
                {connected
                  ? 'The stored key is encrypted and never shown again. Type a new one only to replace it.'
                  : 'Identifies this facility on the engine. Every facility gets its own key.'}
              </p>
            </div>
            {!status?.webhook_secret_set && (
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Webhook secret (product-wide, once)
                </label>
                <input value={secret} onChange={(e) => setSecret(e.target.value)}
                  placeholder="Paste the webhook secret your provider issued"
                  autoComplete="off" spellCheck={false} className={inputCls} />
                <p className="text-[11px] text-slate-400 mt-1">
                  Lets this product verify the texts and emails the engine
                  asks it to send, so nothing else can pose as the engine.
                  Entered once — it covers every facility.
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button type="button" onClick={connect} disabled={busy}
                className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Verifying…'
                  : connected && !apiKey.trim() ? 'Verify connection'
                    : connected ? 'Replace key and verify'
                      : 'Connect and verify'}
              </button>
            </div>
          </div>

          {/* The verification proof stays on screen after connecting —
              "connect & verify" is only meaningful if you see the result. */}
          {result && result.checks && (
            <div className="mt-3 text-[12px] text-slate-600 space-y-0.5 border-t border-slate-100 pt-2.5">
              <p>{result.checks.reachable ? '✓' : '✗'} Engine reachable — marketing tenant handshake v1</p>
              <p>
                {result.checks.key_accepted ? '✓' : '✗'} Key accepted
                {result.checks.key_accepted && result.tenant_name
                  ? ` — ${result.tenant_name}` : ''}
              </p>
              {result.checks.key_accepted && (
                <p>✓ {result.checks.tasks_waiting} marketing tasks waiting</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MasConnectionCard;
